import { HOME_ROUTE, MY_RECO_ROUTE, type AppRoute } from '@/constants/routes';

export interface NotificationTarget {
  route: AppRoute;
  /** Circle to land on via uiSignals.landOnCircleId (null = keep current). */
  landOnCircleId: number | null;
  /**
   * reco-comment-replies — the exact conversation to open, when the push is
   * about one. Both halves are required: a reco without its circle is not a
   * thread (a reco lives in several circles at once), and landing on the circle
   * alone leaves the reader hunting for the reco in a paginated deck.
   */
  thread?: { recommendationId: number; groupId: number };
}

/**
 * A2 — map a push payload (`data` from the backend triggers) to an in-app
 * destination. Pure and defensive: unknown types return null (no navigation),
 * malformed ids never produce NaN. There is no reco-detail screen in the web
 * app, so a new sound lands on the circle's feed where it is the top card;
 * a deleted/left circle is handled by Home's existing fallback effect.
 */
export function resolveNotificationTarget(
  data: Record<string, string> | undefined,
): NotificationTarget | null {
  if (!data?.type) return null;

  // Circle ids are 1-based, so a 0/negative id can only come from a malformed
  // payload — landing on it would select a circle that cannot exist.
  const groupId = Number.parseInt(data.groupId ?? '', 10);
  const landOnCircleId = Number.isFinite(groupId) && groupId > 0 ? groupId : null;

  switch (data.type) {
    case 'new_group_recommendation':
    case 'member_accepted':
    case 'join_request':
      return { route: HOME_ROUTE, landOnCircleId };
    // Someone reacted to / rebounded one of MY sounds → its impact lives on My Reco.
    case 'reaction_received':
    case 'rebound_received':
      return { route: MY_RECO_ROUTE, landOnCircleId: null };
    case 'reply_received': {
      const recoId = Number.parseInt(data.recommendationId ?? '', 10);
      const hasThread = Number.isFinite(recoId) && recoId > 0 && landOnCircleId !== null;
      return {
        route: HOME_ROUTE,
        landOnCircleId,
        // Half a payload opens no thread rather than the wrong one.
        ...(hasThread
          ? { thread: { recommendationId: recoId, groupId: landOnCircleId as number } }
          : {}),
      };
    }
    case 'daily_recommendations':
      return { route: HOME_ROUTE, landOnCircleId: null };
    default:
      return null;
  }
}
